import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  ResponseErrorWithHandled,
  ResponseSuccess,
} from '../../shared/types';
import type {
  DisconnectDwdEmailPayload,
  DwdWorkspaceListItem,
  GetConnectedEmailsPayload,
  GetDwdSyncPreviewRequestPayload,
  ReconnectDwdEmailPayload,
  SyncDwdWorkspaceRequestPayload,
} from './types';
import {
  createDwdWorkspace,
  createMicrosoftWorkspace,
  disconnectDwdEmailAccount,
  deleteDwdEmailAccount,
  getConnectedEmails,
  getDwdWorkspaceSyncPreview,
  getDwdWorkspaces,
  getMicrosoftBulkConsentUrl,
  reconnectDwdEmailAccount,
  syncDwdWorkspace,
} from './helpers/dwd-workspace.api';
import {
  getConnectedEmailsFromResponse,
  getConsentUrlFromResponse,
  getWorkspaceIdFromResponse,
  getDomainEmailsKey,
  getWorkspacesFromResponse,
} from './helpers/dwd-workspace-response';

export const getDwdWorkspacesRequest = createAsyncThunk<
  DwdWorkspaceListItem[],
  void,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/getDwdWorkspaces', async (_, thunkAPI) => {
  try {
    const response = await getDwdWorkspaces();
    return getWorkspacesFromResponse(response);
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const createDwdWorkspaceRequest = createAsyncThunk<
  ResponseSuccess,
  { adminEmail: string },
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/createDwdWorkspace', async ({ adminEmail }, thunkAPI) => {
  try {
    return await createDwdWorkspace(adminEmail);
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const getDwdSyncPreviewRequest = createAsyncThunk<
  ResponseSuccess,
  GetDwdSyncPreviewRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/getDwdSyncPreview', async (payload, thunkAPI) => {
  try {
    const { workspaceId, page, limit, search, domain, isFirstSync } = payload;
    return await getDwdWorkspaceSyncPreview(workspaceId, {
      page,
      limit,
      search,
      domain,
      isFirstSync,
    });
  } catch (error) {
    return thunkAPI.rejectWithValue({
      ...(error as ResponseErrorWithHandled),
      isHandled: true,
    });
  }
});

export const syncDwdWorkspaceRequest = createAsyncThunk<
  ResponseSuccess,
  SyncDwdWorkspaceRequestPayload,
  { rejectValue: ResponseErrorWithHandled }
>(
  'connectMailbox/syncDwdWorkspace',
  async ({ workspaceId, payload }, thunkAPI) => {
    try {
      return await syncDwdWorkspace(workspaceId, payload);
    } catch (error) {
      return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
    }
  },
);

export const createMicrosoftWorkspaceRequest = createAsyncThunk<
  string,
  { tenantId: string; state: string },
  { rejectValue: ResponseErrorWithHandled }
>(
  'connectMailbox/createMicrosoftWorkspace',
  async ({ tenantId, state }, thunkAPI) => {
    try {
      const response = await createMicrosoftWorkspace(tenantId, state);
      const workspaceId = getWorkspaceIdFromResponse(response as ResponseSuccess);

      if (!workspaceId) {
        return thunkAPI.rejectWithValue({
          message: 'Workspace ID was not returned. Please try again.',
          isHandled: false,
        } as ResponseErrorWithHandled);
      }

      return workspaceId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
    }
  },
);

export const getMicrosoftBulkConsentUrlRequest = createAsyncThunk<
  string,
  void,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/getMicrosoftBulkConsentUrl', async (_, thunkAPI) => {
  try {
    const response = await getMicrosoftBulkConsentUrl();
    const consentUrl = getConsentUrlFromResponse(response);

    if (!consentUrl) {
      return thunkAPI.rejectWithValue({
        message: 'Consent URL was not returned. Please try again.',
        isHandled: false,
      } as ResponseErrorWithHandled);
    }

    return consentUrl;
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const getConnectedEmailsRequest = createAsyncThunk<
  { key: string; emails: ReturnType<typeof getConnectedEmailsFromResponse> },
  GetConnectedEmailsPayload,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/getConnectedEmails', async (payload, thunkAPI) => {
  try {
    const { workspaceId, domain, page, limit } = payload;
    const response = await getConnectedEmails(workspaceId, {
      domain,
      page,
      limit,
    });
    const emails = getConnectedEmailsFromResponse(response);

    if (!emails) {
      return thunkAPI.rejectWithValue({
        message: 'Connected emails were not returned. Please try again.',
        isHandled: false,
      } as ResponseErrorWithHandled);
    }

    const key = getDomainEmailsKey(workspaceId, domain?.trim() || 'all');

    return { key, emails };
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const disconnectDwdEmailRequest = createAsyncThunk<
  ResponseSuccess,
  DisconnectDwdEmailPayload,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/disconnectDwdEmail', async (payload, thunkAPI) => {
  try {
    const { workspaceId, emailAccountId, actionType } = payload;

    if (actionType === 'delete') {
      return await deleteDwdEmailAccount(workspaceId, emailAccountId);
    }

    return await disconnectDwdEmailAccount(workspaceId, emailAccountId);
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});

export const reconnectDwdEmailRequest = createAsyncThunk<
  ResponseSuccess,
  ReconnectDwdEmailPayload,
  { rejectValue: ResponseErrorWithHandled }
>('connectMailbox/reconnectDwdEmail', async (payload, thunkAPI) => {
  try {
    const { workspaceId, email } = payload;
    return await reconnectDwdEmailAccount(workspaceId, email);
  } catch (error) {
    return thunkAPI.rejectWithValue(error as ResponseErrorWithHandled);
  }
});
